// LT-611 — public demo-call widget hook (GTM §7).
//
// Owns the demo widget's two call paths from the public landing surface:
//   * WebRTC — joins a LiveKit room directly (mic, hidden audio el, duration
//     cap, agent-join watchdog), reusing the proven lifecycle from the in-app
//     useWebTestAgentModal.
//   * PSTN — POSTs the request; the visitor's phone rings. No client-side
//     room; settlement happens server-side via the gateway webhook.
//
// Also fetches the public context catalogue on mount and reports the kill
// switch so the widget can hide itself when the demo is disabled.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, type RemoteTrack } from 'livekit-client';

import { demoAPI, extractErrorMessage, type DemoContext } from '../../services/api';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';

export type DemoPhase =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'ended'
  | 'error'
  | 'phone_submitting'
  | 'phone_sent';

const WARNING_LEAD_SECONDS = 30;
// Mirror the in-app watchdog: gateway can 200 the WEB session yet its agent
// worker may never join the room (capacity / OOM). Bail with a clear, retryable
// error rather than silence until the duration cap.
const AGENT_JOIN_TIMEOUT_MS = 12000;

export interface UseDemoCallResult {
  // Catalogue / availability
  ready: boolean;
  enabled: boolean;
  contexts: DemoContext[];
  maxDurationSeconds: number;
  freetextMaxChars: number;
  phoneExample: string;
  // Call state
  phase: DemoPhase;
  error: string | null;
  info: string | null;
  callDuration: number;
  secondsRemaining: number | null;
  showWarning: boolean;
  isMuted: boolean;
  micPermissionDenied: boolean;
  // Actions
  startWebCall: (presetKey: string | null, contextText: string | null) => Promise<void>;
  startPhoneCall: (
    phone: string,
    consent: boolean,
    presetKey: string | null,
    contextText: string | null,
  ) => Promise<void>;
  toggleMute: () => Promise<void>;
  endCall: () => Promise<void>;
  reset: () => void;
  formatDuration: (seconds: number) => string;
}

export function useDemoCall(): UseDemoCallResult {
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [contexts, setContexts] = useState<DemoContext[]>([]);
  const [maxDurationSeconds, setMaxDurationSeconds] = useState(180);
  const [freetextMaxChars, setFreetextMaxChars] = useState(600);
  const [phoneExample, setPhoneExample] = useState('');

  const [phase, setPhase] = useState<DemoPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [activeMaxDuration, setActiveMaxDuration] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);

  const roomRef = useRef<Room | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxDurationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endFiredRef = useRef(false);
  const agentAudioSeenRef = useRef(false);

  const secondsRemaining =
    activeMaxDuration === null ? null : Math.max(0, activeMaxDuration - callDuration);
  const showWarning =
    phase === 'active'
    && secondsRemaining !== null
    && secondsRemaining > 0
    && secondsRemaining <= WARNING_LEAD_SECONDS;

  // --- Catalogue ---
  useEffect(() => {
    let cancelled = false;
    demoAPI
      .getContexts()
      .then((resp) => {
        if (cancelled) return;
        setEnabled(Boolean(resp.enabled));
        setContexts(resp.contexts || []);
        if (resp.max_duration_seconds) setMaxDurationSeconds(resp.max_duration_seconds);
        if (resp.freetext_max_chars) setFreetextMaxChars(resp.freetext_max_chars);
        if (resp.phone_example) setPhoneExample(resp.phone_example);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stopTimers = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }
    if (watchdogTimeoutRef.current) {
      clearTimeout(watchdogTimeoutRef.current);
      watchdogTimeoutRef.current = null;
    }
  }, []);

  const teardownRoom = useCallback(async () => {
    stopTimers();
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        audioElRef.current.srcObject = null;
      } catch {
        /* detached */
      }
      audioElRef.current = null;
    }
    if (roomRef.current) {
      try {
        await roomRef.current.disconnect();
      } catch {
        /* already gone */
      }
      roomRef.current = null;
    }
  }, [stopTimers]);

  const settleOnce = useCallback(async () => {
    if (endFiredRef.current) return;
    if (!sessionIdRef.current || !startTimeRef.current) return;
    endFiredRef.current = true;
    const duration = Math.max(
      0,
      Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000),
    );
    try {
      await demoAPI.endWebCall(sessionIdRef.current, duration);
    } catch {
      /* best-effort; server-side webhook is the backstop */
    }
    track(AnalyticsEvents.LANDING_DEMO_COMPLETE, { mode: 'web', duration_seconds: duration });
  }, []);

  const startWebCall = useCallback(
    async (presetKey: string | null, contextText: string | null) => {
      setError(null);
      setInfo(null);
      setMicPermissionDenied(false);
      setCallDuration(0);
      setIsMuted(false);
      setPhase('connecting');
      track(AnalyticsEvents.LANDING_DEMO_PLAY, { mode: 'web' });

      try {
        const resp = await demoAPI.startWebCall({
          preset_key: presetKey,
          context_text: contextText,
        });
        if (!resp?.success || !resp.session_id || !resp.livekit_url || !resp.participant_token) {
          throw new Error('Could not start the demo call.');
        }
        sessionIdRef.current = resp.session_id;
        setActiveMaxDuration(resp.max_duration_seconds ?? maxDurationSeconds);
        endFiredRef.current = false;
        agentAudioSeenRef.current = false;

        const room = new Room({ adaptiveStream: true });
        roomRef.current = room;

        room.on(RoomEvent.TrackSubscribed, (remoteTrack: RemoteTrack) => {
          if (remoteTrack.kind === 'audio') {
            agentAudioSeenRef.current = true;
            if (watchdogTimeoutRef.current) {
              clearTimeout(watchdogTimeoutRef.current);
              watchdogTimeoutRef.current = null;
            }
            const el = remoteTrack.attach() as HTMLAudioElement;
            el.autoplay = true;
            el.style.display = 'none';
            document.body.appendChild(el);
            audioElRef.current = el;
          }
        });

        room.on(RoomEvent.Disconnected, () => {
          stopTimers();
          settleOnce().finally(() => {
            setPhase((p) => (p === 'error' ? 'error' : 'ended'));
          });
        });

        await room.connect(resp.livekit_url, resp.participant_token);

        try {
          await room.localParticipant.setMicrophoneEnabled(true);
        } catch (micErr: unknown) {
          setMicPermissionDenied(true);
          setError(
            micErr instanceof Error && micErr.message
              ? micErr.message
              : 'Microphone access was denied.',
          );
          setPhase('error');
          await teardownRoom();
          return;
        }

        startTimeRef.current = new Date();
        durationIntervalRef.current = setInterval(() => {
          if (startTimeRef.current) {
            setCallDuration(Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000));
          }
        }, 1000);

        const cap = resp.max_duration_seconds ?? maxDurationSeconds;
        if (cap && cap > 0) {
          maxDurationTimeoutRef.current = setTimeout(() => {
            roomRef.current?.disconnect().catch(() => {
              /* falls into onDisconnected */
            });
          }, cap * 1000);
        }

        setPhase('active');

        watchdogTimeoutRef.current = setTimeout(() => {
          if (agentAudioSeenRef.current) return;
          setError('The agent could not connect — the line may be busy. Please try again in a moment.');
          setPhase('error');
          void teardownRoom();
        }, AGENT_JOIN_TIMEOUT_MS);
      } catch (e: unknown) {
        setError(extractErrorMessage(e, 'Could not start the demo call.'));
        setPhase('error');
        await teardownRoom();
      }
    },
    [maxDurationSeconds, settleOnce, stopTimers, teardownRoom],
  );

  const startPhoneCall = useCallback(
    async (
      phone: string,
      consent: boolean,
      presetKey: string | null,
      contextText: string | null,
    ) => {
      setError(null);
      setInfo(null);
      setPhase('phone_submitting');
      track(AnalyticsEvents.LANDING_DEMO_PLAY, { mode: 'phone' });
      try {
        const resp = await demoAPI.startPhoneCall({
          phone,
          consent,
          preset_key: presetKey,
          context_text: contextText,
        });
        if (!resp?.success) {
          throw new Error('Could not place the demo call.');
        }
        setInfo(resp.message || 'Calling you now — your phone should ring shortly.');
        setPhase('phone_sent');
      } catch (e: unknown) {
        setError(extractErrorMessage(e, 'Could not place the demo call.'));
        setPhase('error');
      }
    },
    [],
  );

  const toggleMute = useCallback(async () => {
    if (!roomRef.current || phase !== 'active') return;
    const next = !isMuted;
    try {
      await roomRef.current.localParticipant.setMicrophoneEnabled(!next);
      setIsMuted(next);
    } catch {
      /* leave toggle alone on error */
    }
  }, [isMuted, phase]);

  const endCall = useCallback(async () => {
    if (phase === 'ended' || phase === 'idle') return;
    setPhase('ended');
    await teardownRoom();
    await settleOnce();
  }, [phase, settleOnce, teardownRoom]);

  const reset = useCallback(() => {
    void teardownRoom();
    sessionIdRef.current = null;
    startTimeRef.current = null;
    endFiredRef.current = false;
    agentAudioSeenRef.current = false;
    setPhase('idle');
    setError(null);
    setInfo(null);
    setCallDuration(0);
    setActiveMaxDuration(null);
    setIsMuted(false);
    setMicPermissionDenied(false);
  }, [teardownRoom]);

  // Teardown on unmount.
  useEffect(() => {
    return () => {
      void teardownRoom();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return {
    ready,
    enabled,
    contexts,
    maxDurationSeconds,
    freetextMaxChars,
    phoneExample,
    phase,
    error,
    info,
    callDuration,
    secondsRemaining,
    showWarning,
    isMuted,
    micPermissionDenied,
    startWebCall,
    startPhoneCall,
    toggleMute,
    endCall,
    reset,
    formatDuration,
  };
}
